import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import { task, dropTask } from 'ember-concurrency';
import { getOwner } from '@ember/application';
import isDestroyed from 'cio/utils/is-destroyed';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';
import CampaignNavStepController from 'cio/controllers/campaign-nav-step';
import { CampaignSetupStep } from 'cio/utils/campaign-navigation';
import { CampaignUpdateTypes } from 'cio/models/campaign';
import TagModel from 'cio/models/tag';

export default class SetupController extends CampaignNavStepController {
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service auth!: AuthService;

  @computed('router.currentRouteName', 'navSteps.@each.path')
  get currentNavStep() {
    return this.navSteps.find((step: CampaignSetupStep) => this.router.isActive(step.path));
  }

  @computed('router.currentRouteName')
  get isWorkflowRoute() {
    return this.router.currentRouteName.includes('workflow.actions');
  }

  @computed('currentNavStep')
  get currentNavStepController() {
    if (!this.currentNavStep) {
      return;
    }

    let controllerName = this.currentNavStep.path;
    if (!controllerName.includes('workflow.actions')) {
      controllerName += '.index';
    }
    let controller = getOwner(this).lookup(`controller:${controllerName}`) as any;

    // if setup route was visited before, it already has "controller.model" set
    // unless exact same campaign is accessed without switching environments, it can be:
    // - different campaign
    // - destroyed Ember Data record - after data store clean-up when switching environments
    // don't return controller until it has updated, valid model
    if (!controller?.model || isDestroyed(controller.model) || controller.model.id !== this.model.id) {
      return;
    }

    return controller;
  }

  @computed('model.type')
  get _modelRoute() {
    return this.model.isApiTriggered ? 'broadcasts.list.api-triggered' : 'campaigns';
  }

  @computed('model.type')
  get _modelTitle() {
    return this.model.isApiTriggered ? 'Broadcasts' : 'Campaigns';
  }

  @computed('model.type')
  get singleModelTitle() {
    return this.model.isApiTriggered ? 'broadcast' : 'campaign';
  }

  @computed('currentNavStepController.{isDirty,isInvalid}', 'hasReadOnlyPermission')
  get isSaveable() {
    return (
      this.currentNavStepController.isDirty && !this.currentNavStepController.isInvalid && !this.hasReadOnlyPermission
    );
  }

  get hasReadOnlyPermission() {
    if (this.model.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  saveCampaignTask = task(async (name: string, description: string) => {
    this.model.setProperties({
      name,
      description,
      updateType: CampaignUpdateTypes.Main,
    });

    return await this.model.save();
  });

  saveAndNextTask = task(async () => {
    await this.currentNavStepController.saveCampaignTask.perform();

    this.router.transitionTo((this.nextNavStep as unknown as Record<string, any>).path, this.model.id);
  });

  deleteCampaignTask = dropTask(async () => {
    try {
      await this.model.destroyRecord();
      // [note] An API triggered broadcast is a campaign for now. We have duplicated the flow, the routes,
      // but they are in different sections, so we want to redirect users to the right section based on the type.
      //
      // This should be temporary. Remove this once we have a single broadcast object.
      this.router.transitionTo(this.model.isApiTriggered ? 'broadcasts.list.api-triggered' : 'campaigns');
    } catch (response) {
      this.model.rollbackAttributes();
      this.apiErrors.report(response);
    }
  });

  @action
  addTag(tag: TagModel) {
    return this.model.addTags([tag]);
  }

  @action
  removeTag(tag: TagModel) {
    return this.model.removeTag(tag);
  }
}
